import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateTicket, extractErrorMessage } from '@/queries/ticketMutations'
import { loadDraft, clearDraft, useDraftPersist } from '@/hooks/useDraft'
import { SuggestedArticles } from '@/features/kb/SuggestedArticles'

const schema = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1),
})
type FormVals = z.infer<typeof schema>

const DRAFT_KEY = 'new'

function loadNewDraft(): FormVals {
  try {
    const parsed = JSON.parse(loadDraft(DRAFT_KEY) || '{}') as Partial<FormVals>
    return { subject: parsed.subject ?? '', body: parsed.body ?? '' }
  } catch {
    return { subject: '', body: '' }
  }
}

export function CreateTicketForm() {
  const create = useCreateTicket()
  const navigate = useNavigate()
  const form = useForm<FormVals>({ resolver: zodResolver(schema), defaultValues: loadNewDraft() })
  const subject = form.watch('subject')
  const body = form.watch('body')
  // Persist while typing; empty form clears the entry.
  useDraftPersist(DRAFT_KEY, subject.trim() || body.trim() ? JSON.stringify({ subject, body }) : '')

  const onSubmit = form.handleSubmit(async values => {
    try {
      const ticket = await create.mutateAsync(values)
      clearDraft(DRAFT_KEY)
      toast.success('Ticket opened')
      navigate({ to: `/tickets/${ticket._id}` as never })
    } catch (err) {
      toast.error(extractErrorMessage(err, "Your ticket couldn't be sent. Please try again."))
    }
  })

  return (
    <form onSubmit={onSubmit} className="container mx-auto max-w-2xl p-6 space-y-4">
      <h1 className="text-2xl font-semibold">New Ticket</h1>
      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        <Input id="subject" {...form.register('subject')} />
        {form.formState.errors.subject && <p className="text-sm text-destructive">{form.formState.errors.subject.message}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="body">Describe the issue</Label>
        <Textarea id="body" rows={8} {...form.register('body')} />
        {form.formState.errors.body && <p className="text-sm text-destructive">{form.formState.errors.body.message}</p>}
      </div>

      <SuggestedArticles subject={subject} body={body} />

      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Sending…' : 'Open ticket'}
      </Button>
    </form>
  )
}
